import { Component, Input, OnChanges, TemplateRef } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CommonField, CustomField, Field } from '@vendasta/sales-orders';
import { BehaviorSubject } from 'rxjs';
import { DropDownFormSectionData, FieldBase, User } from '../..';
import {
  CommonFormData,
  CommonFormSectionInterface,
  CustomFieldsAnswer,
  CustomFieldsAnswers,
  OrderFormFieldOptionsType,
  OrderFormInterface,
  OrderFormOptionsInterface,
  ProductInfoInterface,
  ProductOrderFormSectionData,
} from './interface';
import { OrderFormService } from './order-form.service';

@Component({
  styleUrls: ['order-form.component.scss'],
  templateUrl: 'order-form.component.html',
  providers: [],
  selector: 'store-order-form',
})
export class OrderFormComponent implements OnChanges {
  @Input() commonData: CommonFormData;
  @Input() answers: CustomFieldsAnswers[];
  @Input() parentForm: UntypedFormGroup;
  @Input() fileUploadUrl: string;
  @Input() orderForms: OrderFormInterface[];
  @Input() productInfo: ProductInfoInterface[];
  @Input() users: User[];
  @Input() extraFields: OrderFormFieldOptionsType[];
  @Input() extraFieldsAnswers: CustomFieldsAnswer[];
  @Input() orderFormOptions: OrderFormOptionsInterface;
  @Input() businessId: string;
  @Input() hideOrderFormTitle = false;
  @Input() extraHeaderContent: TemplateRef<any>;
  @Input() bottomActionsContent: TemplateRef<any>;

  commonFormSections: CommonFormSectionInterface[];
  customFormSections: ProductOrderFormSectionData[];
  extraFieldsSection: DropDownFormSectionData;

  dropdownOpen$$ = new BehaviorSubject<boolean>(false);
  dropdownOpen$ = this.dropdownOpen$$.asObservable();

  constructor(private translateService: TranslateService) {}

  ngOnChanges(): void {
    const options = new OrderFormOptions(this.orderFormOptions || {});

    const contactIdLabel = this.translateService.instant('FRONTEND.STORE.ORDER_FORM.CONTACT_LABEL');
    const contactIdDescription = this.translateService.instant('FRONTEND.STORE.ORDER_FORM.CONTACT_DESCRIPTION');

    this.commonFormSections = OrderFormService.getCommonFormSections(
      this.orderForms,
      this.commonData,
      options,
      contactIdDescription,
      contactIdLabel,
    );
    this.customFormSections = OrderFormService.getCustomFormSections(
      this.businessId,
      this.orderForms,
      this.productInfo,
      this.parentForm,
      this.fileUploadUrl,
      this.users,
      this.answers,
      options,
    );

    this.extraFieldsSection = OrderFormService.getExtraFieldsSection(
      this.extraFields,
      this.parentForm,
      this.fileUploadUrl,
      this.users,
      this.extraFieldsAnswers,
      options,
    );

    this.changeReadOnly(this.orderFormOptions.readOnly);
  }

  /**
   * returns the data from common form fields in a format that is usable by sales orders
   */
  public getCommonFormData(): CommonField[] {
    return OrderFormService.getCommonFieldsData(this.commonFormSections);
  }

  /**
   * returns the data from custom forms in a format that is usable by sales orders
   */
  public getCustomFormData(): CustomField[] {
    return OrderFormService.getCustomFieldsData(this.customFormSections);
  }

  /**
   * returns the data from extra fields in a format that is usable by sales orders
   */
  public getExtraFieldsData(): Field[] {
    return OrderFormService.getExtraFieldsData(this.extraFieldsSection);
  }

  /**
   * returns true if any fields in the custom form sections are required and office use/editable only
   */
  public orderFormRequiresPartnerInput(): boolean {
    return OrderFormService.containsFieldsThatMatch(this.customFormSections, (f: FieldBase<any>) => {
      return f.required && (f.forOfficeUseOnly || f.officeEditableOnly);
    });
  }

  /**
   * changes the read only state of the sales order form
   */
  public changeReadOnly(readOnly: boolean): void {
    this.orderFormOptions.readOnly = readOnly;

    for (const section of this.customFormSections) {
      if (section.primarySection) {
        section.primarySection.displayAutoTitle = !readOnly;
      }
      for (const subsection of section.subsections) {
        subsection.displayAutoTitle = !readOnly;
      }
    }

    readOnly ? this.parentForm.disable() : this.parentForm.enable();
  }

  public openDropdown(): void {
    this.dropdownOpen$$.next(true);
  }
}

export class OrderFormOptions implements OrderFormOptionsInterface {
  bypassRequiredQuestions: boolean;
  readOnly: boolean;
  showOfficeUseQuestions: boolean;

  constructor(i: OrderFormOptionsInterface) {
    this.bypassRequiredQuestions = !!i.bypassRequiredQuestions;
    this.readOnly = !!i.readOnly;
    this.showOfficeUseQuestions = !!i.showOfficeUseQuestions;
  }
}
